import React, { useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import { Form, Col } from 'react-bootstrap';
import Modal from '../../components/Modal';
import Button from '../../components/Button';
import { CadernoLPMARegistroVisitas } from '../../models/modelsCadernoLPMA';
import { STRINGS_CADERNOLPMA } from '../../util/cadernoLPMA/string';

type RegistroVisitasFormValues = CadernoLPMARegistroVisitas & {
	dataVisita?: Date | string | null;
};

interface RegistroVisitasModalProps {
	show: boolean;
	onHide: () => void;
	onSubmit: (info: CadernoLPMARegistroVisitas) => void;
	initialData?: CadernoLPMARegistroVisitas;
}

const parseDateValue = (valor: any): Date | null => {
	if (!valor && valor !== 0) {
		return null;
	}

	if (valor instanceof Date) {
		return Number.isNaN(valor.getTime()) ? null : valor;
	}

	if (typeof valor === 'string') {
		const texto = valor.trim();
		if (!texto) {
			return null;
		}

		const data = new Date(texto);
		return Number.isNaN(data.getTime()) ? null : data;
	}

	if (typeof valor === 'number') {
		const data = new Date(valor);
		return Number.isNaN(data.getTime()) ? null : data;
	}

	return null;
};

const defaultValues: RegistroVisitasFormValues = {
	id: 0,
	cadernoLPMAId: 0,
	nomeVisitante: '',
	motivoVisita: '',
	dataVisita: null,
};

const converterValoresIniciais = (
	dados?: CadernoLPMARegistroVisitas | null,
): RegistroVisitasFormValues => {
	if (!dados) {
		return { ...defaultValues };
	}

	return {
		...defaultValues,
		...dados,
		dataVisita: parseDateValue(dados.dataVisita),
	};
};

const RegistroVisitasModal: React.FC<RegistroVisitasModalProps> = ({
	show,
	onHide,
	onSubmit,
	initialData,
}) => {
	const estaEditando = useMemo(() => Boolean(initialData?.id), [initialData]);

	const { control, handleSubmit, reset, register } = useForm<RegistroVisitasFormValues>({
		defaultValues,
		mode: 'all',
	});

	useEffect(() => {
		if (!show) {
			reset(defaultValues);
			return;
		}

		reset(converterValoresIniciais(initialData));
	}, [show, initialData, reset]);

	const handleInternalSubmit = handleSubmit((dados) => {
		const processado: CadernoLPMARegistroVisitas = {
			...dados,
			id: dados.id ?? initialData?.id ?? 0,
			cadernoLPMAId: dados.cadernoLPMAId ?? initialData?.cadernoLPMAId ?? 0,
			dataVisita: parseDateValue(dados.dataVisita),
		};

		onSubmit(processado);
	});

	return (
		<Modal
			show={show}
			onHide={onHide}
			title={STRINGS_CADERNOLPMA.REGISTROVISITAS_MODAL_TITLE}
			bodyClassName="caderno-lpma-modal-body"
		>
			<Form onSubmit={handleInternalSubmit} autoComplete="off">
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Nome do Visitante</Form.Label>
						<Controller
							control={control}
							name="nomeVisitante"
							render={({ value, onChange }) => (
								<Form.Control
									placeholder="Insira o nome do visitante"
									value={value ?? ''}
									onChange={(event) => onChange(event.target.value)}
								/>
							)}
						/>
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Motivo da Visita</Form.Label>
						<Controller
							control={control}
							name="motivoVisita"
							render={({ value, onChange }) => (
								<Form.Control
									placeholder="Insira o motivo da visita"
									value={value ?? ''}
									onChange={(event) => onChange(event.target.value)}
								/>
							)}
						/>
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={6}>
						<Form.Label>Data da Visita</Form.Label>
						<Controller
							control={control}
							name="dataVisita"
							render={({ value, onChange }) => (
								<DatePicker
									selected={parseDateValue(value)}
									onChange={onChange}
									className="input-custom-to-date"
									locale="pt-BR"
									dateFormat="dd/MM/yyyy"
									placeholderText="Selecionar a data"
								/>
							)}
						/>
					</Form.Group>
				</Form.Row>
				<div className="button-position">
					<Button title="Cancelar" contained={false} type="button" onClick={onHide} />
					<Button title={estaEditando ? 'Atualizar' : 'Inserir'} contained type="submit" />
				</div>
			</Form>
		</Modal>
	);
};

export default RegistroVisitasModal;

